import { useState } from 'react';
import { useDataAuth } from './useDataAuth';
import { fetchClientEmails, fetchLastInvoiceEmails, fetchSendInvoice, insert, update } from '../services/invoice.services';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useEmailRecipients() {
    const [{ userSession }] = useDataAuth();

    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [clientEmails, setClientEmails] = useState([]);
    const [lastInvoiceEmails, setLastInvoiceEmails] = useState([]);

    // ── internal dialog state ─────────────────────────────────────────────────
    const [catalogue, setCatalogue] = useState([]);
    const [selected, setSelected] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [newEmailError, setNewEmailError] = useState('');

    // ── open: load emails and initialise dialog ───────────────────────────────
    const openDialog = async (officeId) => {
        try {
            const [clientEmailsRes, lastInvoiceEmailsRes] = await Promise.all([
                officeId
                    ? fetchClientEmails(officeId, userSession.token)
                    : Promise.resolve({ data: [] }),
                officeId
                    ? fetchLastInvoiceEmails(officeId, userSession.token)
                    : Promise.resolve({ data: [] }),
            ]);

            const rawClientEmails = clientEmailsRes?.data?.[0]?.email || '';
            const parsedClientEmails = typeof rawClientEmails === 'string'
                ? rawClientEmails.split(',').map(e => e.trim()).filter(Boolean)
                : rawClientEmails;

            const rawLastEmails = lastInvoiceEmailsRes?.data?.[0]?.email_recipients || '';
            const parsedLastEmails = typeof rawLastEmails === 'string'
                ? rawLastEmails.split(',').map(e => e.trim()).filter(Boolean)
                : rawLastEmails;

            setClientEmails(parsedClientEmails);
            setLastInvoiceEmails(parsedLastEmails);

            // Build catalogue and pre-selection
            const base = Array.from(new Set([...parsedClientEmails, ...parsedLastEmails])).filter(Boolean);
            setCatalogue(base);

            const preSelected = parsedLastEmails.length > 0 ? parsedLastEmails : parsedClientEmails;
            setSelected(Array.from(new Set(preSelected)).filter(Boolean));

            setNewEmail('');
            setNewEmailError('');
            setOpenEmailDialog(true);
        } catch (err) {
            console.error('Error loading email data:', err);
            setCatalogue([]);
            setSelected([]);
            setClientEmails([]);
            setLastInvoiceEmails([]);
            setOpenEmailDialog(true);
        }
    };

    const closeDialog = () => {
        setOpenEmailDialog(false);
        setNewEmail('');
        setNewEmailError('');
    };

    // ── new email field ───────────────────────────────────────────────────────
    const handleNewEmailChange = (value) => {
        setNewEmail(value);
        if (newEmailError) setNewEmailError('');
    };

    const addEmail = (errorMsg = 'Correo electrónico no válido') => {
        const trimmed = newEmail.trim().toLowerCase();
        if (!trimmed) return;

        if (!EMAIL_REGEX.test(trimmed)) {
            setNewEmailError(errorMsg);
            return;
        }

        setNewEmailError('');

        if (!catalogue.includes(trimmed)) {
            setCatalogue(prev => [...prev, trimmed]);
        }
        if (!selected.includes(trimmed)) {
            setSelected(prev => [...prev, trimmed]);
        }

        setNewEmail('');
    };

    // ── multi-select ──────────────────────────────────────────────────────────
    const handleSelectChange = (event) => {
        const { value } = event.target;
        setSelected(typeof value === 'string' ? value.split(',') : value);
    };

    const handleRemoveChip = (email) => {
        setSelected(prev => prev.filter(e => e !== email));
    };

    // ── DIAN send flow ────────────────────────────────────────────────────────
    /**
     * @param {object}   params
     * @param {object}   params.invoiceData          - current form state
     * @param {boolean}  params.edit                 - editing an existing invoice
     * @param {function} params.handleDirectInputChange
     * @param {function} params.handleResponseSend   - (response, id) => void
     * @param {function} params.setLoading
     * @returns {string} emailRecipientsStr           - so the caller can use it if needed
     */
    const confirmAndSend = async ({
        invoiceData,
        edit,
        handleDirectInputChange,
        handleResponseSend,
        setLoading,
        action = 'CREATE_JSON_DIAN_INVOICE',   // each layout passes its own action
    }) => {
        closeDialog();

        const emailRecipientsStr = selected.join(',');
        handleDirectInputChange('email_recipients', emailRecipientsStr);
        const json = {
            ...invoiceData,
            retentions: invoiceData.retentions.join(','),
            email_recipients: emailRecipientsStr,
        };
        const data = JSON.stringify(json);

        setLoading(true);
        try {
            if (!edit) {
                const response = await insert(userSession.token, data);
                if (response?.status === 200 && response?.data?.[0]) {
                    const id = response.data[0].id;
                    const responseSend = await fetchSendInvoice(userSession.token, id, action);
                    await handleResponseSend(responseSend, id);
                }
            } else {
                const response = await update(data, userSession.token);
                if (response?.status === 200) {
                    const responseSend = await fetchSendInvoice(
                        userSession.token,
                        invoiceData.id_electronic_document,
                        action
                    );
                    await handleResponseSend(responseSend, invoiceData.id_electronic_document);
                }
            }
        } finally {
            setLoading(false);
        }

        return emailRecipientsStr;
    };

    return {
        // dialog visibility
        openEmailDialog,
        openDialog,
        closeDialog,

        // email lists (for consumers that still need them)
        clientEmails,
        lastInvoiceEmails,

        // catalogue & selection
        catalogue,
        selected,

        // new-email field
        newEmail,
        newEmailError,
        handleNewEmailChange,
        addEmail,

        // multi-select & chips
        handleSelectChange,
        handleRemoveChip,

        // send flow
        confirmAndSend,
    };
}
